/**
 * Login activity card on the account-detail page (OCP-513).
 *
 * Read-only account-takeover surface. Self-contained: loads its own
 * `GET /v1/admin/accounts/:id/login-activity`. Shows the last successful
 * login, the set of countries the account has ever logged in from, and the
 * recent login attempts (incl. failures) with a visual flag on any attempt
 * from a country the account had never successfully logged in from before -
 * the same never-before-seen-country signal that fires the operator Web Push.
 *
 * A load failure surfaces an inline error (this is a security surface - the
 * operator should notice when it can't load) rather than silently hiding.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';
import { GeoIpAttribution } from './GeoIpAttribution';

interface LoginAttemptRow {
  id: string;
  email: string;
  outcome: string;
  factor: string;
  country: string | null;
  ip_address: string | null;
  user_agent: string | null;
  attempted_at: string;
  is_new_country: boolean;
}

interface LoginActivity {
  recent_attempts: LoginAttemptRow[];
  last_successful_login: {
    at: string;
    country: string | null;
    ip_address: string | null;
  } | null;
  known_countries: string[];
}

function fmtUtc(iso: string): string {
  return fmtDateTime(iso);
}

export function LoginActivityCard({ customerId }: { customerId: string }) {
  const [data, setData] = useState<LoginActivity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api<LoginActivity>(`/v1/admin/accounts/${customerId}/login-activity`);
      setData(r);
    } catch (err) {
      setError(err instanceof ApiError ? `Load failed (HTTP ${err.status}).` : 'Load failed.');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Login activity</h2>
        {data && data.known_countries.length > 0 && (
          <span className="text-xs text-gray-500">
            Seen from {data.known_countries.join(', ')}
          </span>
        )}
      </div>

      {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}

      {loading && !data && <div className="mt-3 text-sm text-gray-500">Loading…</div>}

      {data && (
        <div className="mt-3 space-y-3">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            Last successful login:{' '}
            {data.last_successful_login ? (
              <span className="text-gray-900 dark:text-gray-100">
                {fmtUtc(data.last_successful_login.at)}
                {data.last_successful_login.country && ` · ${data.last_successful_login.country}`}
                {data.last_successful_login.ip_address &&
                  ` · ${data.last_successful_login.ip_address}`}
              </span>
            ) : (
              '—'
            )}
          </div>

          {data.recent_attempts.length === 0 ? (
            <div className="rounded border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-4 text-sm text-gray-500">
              No recent login attempts (attempt history is retained for ~24h).
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Time</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Outcome</th>
                    <th className="px-3 py-2">Factor</th>
                    <th className="px-3 py-2">Country</th>
                    <th className="px-3 py-2">IP</th>
                    <th className="px-3 py-2">User agent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {data.recent_attempts.map((a) => (
                    <tr
                      key={a.id}
                      className={
                        a.is_new_country ? 'bg-amber-50 dark:bg-amber-900/20' : undefined
                      }
                    >
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                        {fmtDateTime(a.attempted_at)}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{a.email}</td>
                      <td className="px-3 py-2 text-xs">
                        <OutcomePill outcome={a.outcome} />
                      </td>
                      <td className="px-3 py-2 text-xs">{a.factor}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className="inline-flex items-center gap-1">
                          {a.country ?? '—'}
                          {a.is_new_country && (
                            <span
                              title="Country never seen before for this account"
                              className="rounded border border-amber-300 dark:border-amber-700 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:text-amber-200"
                            >
                              new country
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-500">
                        {a.ip_address ?? '—'}
                      </td>
                      <td
                        className="max-w-[220px] truncate px-3 py-2 text-xs text-gray-500"
                        title={a.user_agent ?? undefined}
                      >
                        {a.user_agent ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <GeoIpAttribution />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OutcomePill({ outcome }: { outcome: string }) {
  const success = outcome === 'success';
  const cls = success
    ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
    : outcome === 'rate_limited'
      ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
      : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200';
  return <span className={`rounded border px-2 py-0.5 text-xs ${cls}`}>{outcome}</span>;
}
